import { Stack, Group, Button, MultiSelect, TextInput, Select, Checkbox, Text } from '@mantine/core'
import dayjs from 'dayjs'
import type { AuthorizationFormState } from './authorizationFormData'
import {
  RECORD_TYPE_OPTIONS,
  PURPOSE_OPTIONS,
  SENSITIVE_TYPE_OPTIONS,
} from './authorizationFormData'

type Props = {
  values: AuthorizationFormState
  update: (patch: Partial<AuthorizationFormState>) => void
  onBack: () => void
  onNext: () => void
}

export const DetailsStep = ({ values, update, onBack, onNext }: Props) => {
  const recordTypesHasOther = values.recordTypes.includes('Other')
  const purposeIsOther = values.purpose === 'Other'

  // ISO yyyy-mm-dd from the date inputs compares correctly as strings.
  const today = dayjs().format('YYYY-MM-DD')

  const dateRangeInvalid =
    values.dateRangeStart.trim().length > 0 &&
    values.dateRangeEnd.trim().length > 0 &&
    values.dateRangeEnd < values.dateRangeStart

  // Records can only have been created in the past, so future dates are invalid.
  const startInFuture = values.dateRangeStart.trim().length > 0 && values.dateRangeStart > today
  const endInFuture = values.dateRangeEnd.trim().length > 0 && values.dateRangeEnd > today

  const canAdvance =
    values.recordTypes.length > 0 &&
    (!recordTypesHasOther || values.recordTypesOther.trim().length > 0) &&
    values.purpose.trim().length > 0 &&
    (!purposeIsOther || values.purposeOther.trim().length > 0) &&
    values.dateRangeStart.trim().length > 0 &&
    values.dateRangeEnd.trim().length > 0 &&
    !dateRangeInvalid &&
    !startInFuture &&
    !endInFuture

  return (
    <Stack gap="lg">
      <MultiSelect
        label="Records being requested"
        required
        data={[...RECORD_TYPE_OPTIONS]}
        value={values.recordTypes}
        onChange={(v) => update({ recordTypes: v })}
        placeholder="Select record types"
      />
      {recordTypesHasOther && (
        <TextInput
          label="Please specify other record type(s)"
          required
          placeholder="Describe the records being requested"
          value={values.recordTypesOther}
          onChange={(e) => update({ recordTypesOther: e.currentTarget.value })}
        />
      )}
      <Group grow>
        <TextInput
          label="Date range from"
          type="date"
          required
          max={values.dateRangeEnd && values.dateRangeEnd < today ? values.dateRangeEnd : today}
          value={values.dateRangeStart}
          onChange={(e) => update({ dateRangeStart: e.currentTarget.value })}
          error={startInFuture ? 'Date cannot be in the future' : undefined}
        />
        <TextInput
          label="Date range to"
          type="date"
          required
          min={values.dateRangeStart || undefined}
          max={today}
          value={values.dateRangeEnd}
          onChange={(e) => update({ dateRangeEnd: e.currentTarget.value })}
          error={
            dateRangeInvalid
              ? '"To" date cannot be before the "From" date'
              : endInFuture
                ? 'Date cannot be in the future'
                : undefined
          }
        />
      </Group>
      <Select
        label="Purpose of disclosure"
        required
        data={[...PURPOSE_OPTIONS]}
        value={values.purpose || null}
        onChange={(v) => update({ purpose: v ?? '', purposeOther: '' })}
        placeholder="Select a purpose"
      />
      {purposeIsOther && (
        <TextInput
          label="Please specify other purpose"
          required
          placeholder="Describe the purpose of disclosure"
          value={values.purposeOther}
          onChange={(e) => update({ purposeOther: e.currentTarget.value })}
        />
      )}
      <Stack gap="xs">
        <Text size="sm" fw={500}>
          Sensitive information authorized for release{' '}
          <Text span size="xs" c="dimmed">
            (optional — select all that apply)
          </Text>
        </Text>
        <Checkbox.Group
          value={values.sensitiveTypes}
          onChange={(v) => update({ sensitiveTypes: v })}
        >
          <Stack gap="xs">
            {SENSITIVE_TYPE_OPTIONS.map((opt) => (
              <Checkbox key={opt} value={opt} label={opt} />
            ))}
          </Stack>
        </Checkbox.Group>
      </Stack>
      <Group justify="space-between">
        <Button variant="default" onClick={onBack}>
          Back
        </Button>
        <Button color="teal" onClick={onNext} disabled={!canAdvance}>
          Next: Review
        </Button>
      </Group>
    </Stack>
  )
}
